import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { sendMessage, Message } from '../../services/vertexAgent';
import ChatMessage from '../../components/chat/ChatMessage';
import ChatInput from '../../components/chat/ChatInput';
import SuggestedQueries from '../../components/chat/SuggestedQueries';
import SparklesLogo from '../../components/chat/SparklesLogo';
import './Agents.css';

interface ChatHistory {
  id: string;
  title: string;
  messages: Message[];
  timestamp: number;
}

function getGreetingKey(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'agents.greetingMorning';
  if (hour < 18) return 'agents.greetingAfternoon';
  return 'agents.greetingEvening';
}

export default function Agents() {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);
  const pendingSendRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup on unmount to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (pendingSendRef.current) clearTimeout(pendingSendRef.current);
    };
  }, []);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Load chat history from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('chatHistory');
    if (saved) {
      setChatHistory(JSON.parse(saved));
    }
  }, []);

  // Save current chat to history when messages change
  useEffect(() => {
    if (messages.length > 0) {
      const chatId = currentChatId || Date.now().toString();
      const title = messages[0]?.content.slice(0, 50) + (messages[0]?.content.length > 50 ? '...' : '');

      setChatHistory(prevHistory => {
        const updatedHistory = prevHistory.filter(chat => chat.id !== chatId);
        const newChat: ChatHistory = {
          id: chatId,
          title,
          messages,
          timestamp: Date.now()
        };

        const newHistory = [newChat, ...updatedHistory].slice(0, 20);
        localStorage.setItem('chatHistory', JSON.stringify(newHistory));
        return newHistory;
      });

      if (!currentChatId) {
        setCurrentChatId(chatId);
      }
    }
  }, [messages, currentChatId]);

  const handleSendMessage = async (userMessage: string) => {
    const updatedMessages = [...messages, { role: 'user' as const, content: userMessage }];
    setMessages(updatedMessages);
    setIsLoading(true);

    try {
      const { updatedHistory } = await sendMessage(userMessage, messages);
      setMessages(updatedHistory);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages([
        ...updatedMessages,
        {
          role: 'assistant',
          content: t('agents.errorMessage')
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Deferred send: waits for React state to flush before sending
  const deferSend = (content: string) => {
    pendingSendRef.current = setTimeout(() => {
      if (mountedRef.current) handleSendMessage(content);
    }, 0);
  };

  const handleRetry = (messageIndex: number) => {
    const msg = messages[messageIndex];
    if (msg.role === 'user') {
      // Re-send the user message: remove everything from this message onward
      const prior = messages.slice(0, messageIndex);
      setMessages(prior);
      deferSend(msg.content);
    } else {
      // Retry assistant: re-send the previous user message
      const lastUserIdx = messages.slice(0, messageIndex).findLastIndex(m => m.role === 'user');
      if (lastUserIdx >= 0) {
        const prior = messages.slice(0, lastUserIdx);
        const userMsg = messages[lastUserIdx].content;
        setMessages(prior);
        deferSend(userMsg);
      }
    }
  };

  const handleEdit = (messageIndex: number, content: string) => {
    // Remove everything from this message onward, then send the edited content
    const prior = messages.slice(0, messageIndex);
    setMessages(prior);
    deferSend(content);
  };

  const handleSuggestedQuery = (query: string) => {
    handleSendMessage(query);
  };

  const handleNewChat = () => {
    setMessages([]);
    setCurrentChatId(null);
  };

  const handleLoadChat = (chat: ChatHistory) => {
    setMessages(chat.messages);
    setCurrentChatId(chat.id);
  };

  const handleDeleteChat = (chatId: string) => {
    const newHistory = chatHistory.filter(chat => chat.id !== chatId);
    setChatHistory(newHistory);
    localStorage.setItem('chatHistory', JSON.stringify(newHistory));

    if (currentChatId === chatId) {
      handleNewChat();
    }
  };

  return (
    <div className="agents-page">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`chat-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          title={sidebarOpen ? t('agents.closeSidebar') : t('agents.openSidebar')}
        >
          {sidebarOpen ? '◀' : '▶'}
        </button>
        <div className="sidebar-header">
          <button className="new-chat-button" onClick={handleNewChat}>
            {t('agents.newChat')}
          </button>
        </div>

        {sidebarOpen && (
          <div className="chat-history-list">
            <h3>{t('agents.recent')}</h3>
            {chatHistory.length === 0 ? (
              <div className="no-history">{t('agents.noChatHistory')}</div>
            ) : (
              chatHistory.map((chat) => (
                <div
                  key={chat.id}
                  className={`chat-history-item ${currentChatId === chat.id ? 'active' : ''}`}
                  onClick={() => handleLoadChat(chat)}
                >
                  <div className="chat-title">{chat.title}</div>
                  <button
                    className="delete-chat-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteChat(chat.id);
                    }}
                    title={t('agents.deleteChat')}
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Main chat area */}
      <div className="chat-main">
        <div className="chat-container">
          {messages.length === 0 ? (
            /* Empty state — welcome screen */
            <div className="welcome-screen">
              <div className="welcome-content">
                <SparklesLogo size={64} className="welcome-logo" />
                <h1 className="welcome-greeting">
                  {t(getGreetingKey())}
                </h1>
                <h2 className="welcome-subtitle">
                  {t('agents.welcomeQuestion')}
                </h2>
              </div>

              <div className="welcome-input-area">
                <ChatInput onSend={handleSendMessage} disabled={isLoading} />
              </div>

              <div className="welcome-suggestions">
                <p className="suggestions-label">{t('agents.suggestionsLabel')}</p>
                <SuggestedQueries onQueryClick={handleSuggestedQuery} />
              </div>
            </div>
          ) : (
            /* Active chat */
            <>
              <div className="messages-list">
                {messages.map((msg, idx) => (
                  <ChatMessage
                    key={idx}
                    message={msg}
                    onRetry={() => handleRetry(idx)}
                    onEdit={msg.role === 'user' ? (content) => handleEdit(idx, content) : undefined}
                  />
                ))}

                {isLoading && (
                  <ChatMessage
                    message={{ role: 'assistant', content: t('agents.analyzing') }}
                    isLoading
                  />
                )}

                <div ref={messagesEndRef} />
              </div>

              <div className="chat-input-wrapper">
                <ChatInput onSend={handleSendMessage} disabled={isLoading} />
                <p className="ai-disclaimer">{t('agents.aiDisclaimer')}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
